/**
 * Verify Email Change Page
 * Handles email change verification from email link
 */
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { verifyEmailChange } from '../api/emailChange';
import logo from '../assets/logo.svg';

type VerificationState = 'verifying' | 'success' | 'error';

export default function VerifyEmailChange() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<VerificationState>('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMessage('Invalid verification link. No token provided.');
      return;
    }

    verifyToken(token);
  }, [token]);

  const verifyToken = async (verifyToken: string) => {
    try {
      await verifyEmailChange(verifyToken);
      setState('success');

      // Clear auth tokens (user needs to log in again)
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    } catch (err: any) {
      setState('error');
      const message = err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to verify email change';
      setErrorMessage(message);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-dark-bg to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img src={logo} alt="PARACLETE" className="h-16 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-white">Email Change Verification</h1>
        </div>

        {/* Card */}
        <div className="bg-dark-card/50 backdrop-blur-xl border border-dark-border/50 rounded-2xl shadow-2xl p-8">
          {state === 'verifying' && (
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-accent-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-white mb-2">Verifying...</h2>
              <p className="text-gray-400">Please wait while we verify your email change.</p>
            </div>
          )}

          {state === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Email Changed Successfully!</h2>
              <p className="text-gray-400 mb-6">
                Your email address has been updated. Please log in with your new email address.
              </p>
              <button
                onClick={handleGoToLogin}
                className="w-full py-3 bg-gradient-to-r from-accent-blue to-accent-orange text-white font-semibold rounded-xl hover:shadow-lg transition-all"
              >
                Go to Login
              </button>
            </div>
          )}

          {state === 'error' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Verification Failed</h2>
              <p className="text-gray-400 mb-6">{errorMessage}</p>
              <div className="space-y-3">
                <button
                  onClick={handleGoToLogin}
                  className="w-full py-3 bg-gradient-to-r from-accent-blue to-accent-orange text-white font-semibold rounded-xl hover:shadow-lg transition-all"
                >
                  Go to Login
                </button>
                <p className="text-sm text-gray-500">
                  If this link has expired, you can request a new one from your profile settings after logging in.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
